/**
 * Incremental SSE frame parsing for the Ask answer stream.
 *
 * Split out from httpProvider.ts because it is the one piece of this feature worth unit
 * testing in isolation: `fetch()`, `AbortController`, and a real network response are all
 * hard to fake convincingly, but this function only needs a `ReadableStreamDefaultReader`
 * that yields byte chunks — which a test can construct by hand, INCLUDING splitting a
 * single `data: {...}` line across two chunks, the classic SSE parsing bug this module
 * exists to not have. (No test runner is wired up for apps/platform today — see the
 * VRG-227 report — so this is exercised by a standalone script instead; the function is
 * written to be trivially portable into a real `*.spec.ts` the moment one exists.)
 *
 * Kept format-agnostic about the payload itself: this only extracts the joined `data:`
 * text of each event and yields it as a string. Parsing that string into an
 * `AskStreamEvent` is the caller's job (httpProvider.ts), so a malformed payload can be
 * skipped there without this module needing to know what "malformed" means for this
 * particular wire contract.
 */

/**
 * Read one SSE stream to completion, yielding the raw `data:` payload of each event.
 *
 * SSE frames are separated by a blank line. A frame's `data:` lines are joined with `\n`
 * per the spec (multi-line data), though this API only ever sends one `data:` line per
 * event today. Frame boundaries do not line up with `read()` chunk boundaries — a
 * `data: ` line, or even the two-character `\n\n` terminator itself, can be split across
 * reads — so this buffers whatever hasn't formed a complete frame yet and only emits once
 * a full blank-line-terminated frame has accumulated.
 */
export async function* readSseEvents(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): AsyncGenerator<string, void, undefined> {
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()

    if (value) {
      buffer += decoder.decode(value, { stream: true })
      // Normalize AFTER concatenation, not per-chunk — a chunk boundary can land
      // between the \r and \n of a single line ending, and only the combined buffer is
      // guaranteed to hold both characters together.
      buffer = buffer.replace(/\r\n/g, '\n')
    }

    let boundary: number
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const payload = extractDataPayload(frame)
      if (payload !== undefined) yield payload
    }

    if (done) {
      // A server that closes the connection right after its last frame, with no
      // trailing blank line, would otherwise lose that final event.
      buffer += decoder.decode()
      buffer = buffer.replace(/\r\n/g, '\n')
      const payload = extractDataPayload(buffer)
      if (payload !== undefined) yield payload
      return
    }
  }
}

function extractDataPayload(frame: string): string | undefined {
  const lines = frame
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).replace(/^ /, ''))
  if (lines.length === 0) return undefined
  return lines.join('\n')
}
